import { useEffect, useState, useCallback, useRef } from "react";
import { Outlet, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, MessageSquareText, Users, User, LogOut, ChevronDown, Lock, Clock, Sprout, MessagesSquare, GraduationCap, History as HistoryIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function TrialBar({ user }) {
  const nav = useNavigate();
  if (user.access_state === "paid") {
    return (
      <div className="w-full text-center text-sm py-2" style={{ background: "var(--sage-tint)", color: "var(--sage)" }} data-testid="trial-countdown-bar">
        ✦ You're a DecodeKid Plus member — thank you!
      </div>
    );
  }
  if (user.access_state === "expired") {
    return (
      <div className="w-full text-center text-sm py-2 flex items-center justify-center gap-3" style={{ background: "#F3DEDA", color: "var(--error)" }} data-testid="trial-countdown-bar">
        Your access has ended.
        <button className="link" onClick={() => nav("/app/account")}>Subscribe to continue</button>
      </div>
    );
  }
  const end = user.trial_ends ? new Date(user.trial_ends) : null;
  let label = "Free trial active";
  if (end) {
    const ms = end - new Date();
    const d = Math.max(0, Math.floor(ms / 86400000));
    const h = Math.max(0, Math.floor((ms % 86400000) / 3600000));
    label = `Trial ends in ${d}d ${h}h`;
  }
  return (
    <div className="w-full text-center text-sm py-2 flex items-center justify-center gap-3" style={{ background: "var(--sage-tint)", color: "var(--sage)" }} data-testid="trial-countdown-bar">
      <Clock size={14} /> {label}
      <button className="link" onClick={() => nav("/app/account")}>Subscribe</button>
    </div>
  );
}

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/decode", label: "Decode", icon: MessageSquareText },
  { to: "/app/coach", label: "Coach", icon: MessagesSquare },
  { to: "/app/learn", label: "Learn", icon: GraduationCap },
  { to: "/app/history", label: "Deck", icon: HistoryIcon },
  { to: "/app/children", label: "Family", icon: Users },
  { to: "/app/account", label: "Account", icon: User },
];

export default function AppShell() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  const [locked, setLocked] = useState(false);
  const timer = useRef(null);

  const resetIdle = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setLocked(true), 10 * 60 * 1000);
  }, []);

  useEffect(() => {
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, resetIdle));
    resetIdle();
    return () => { evts.forEach((e) => window.removeEventListener(e, resetIdle)); if (timer.current) clearTimeout(timer.current); };
  }, [resetIdle]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [loc.pathname]);

  if (user && !user.onboarded) return <Navigate to="/app/onboarding" replace />;

  return (
    <div className="min-h-screen bg-[#F4F6F4]">
      <TrialBar user={user} />
      <header className="dk-raised border-b sticky top-0 z-40">
        <div className="dk-container flex items-center justify-between h-16">
          <Link to="/app" className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "var(--sage-tint)" }}><Sprout size={18} style={{ color: "var(--sage)" }} /></span>
            <span className="font-serif text-[18px] font-semibold">Decode<span style={{ color: "var(--accent-strong)" }}>Kid</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => {
              const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`appnav-${n.label.toLowerCase().replace(" ", "-")}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-[#E5E2DC] ${active ? "text-[#B04A39] bg-[#E5E2DC]" : ""}`}>
                  <n.icon size={16} /> {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="relative">
            <button className="btn btn-ghost" onClick={() => setMenu(!menu)} data-testid="account-menu-btn">
              <span className="grid place-items-center w-7 h-7 rounded-full text-white text-xs font-semibold" style={{ background: "var(--sage)" }}>
                {(user.first_name || user.email)[0]?.toUpperCase()}
              </span>
              <ChevronDown size={16} />
            </button>
            {menu && (
              <div className="absolute right-0 mt-2 w-48 dk-card p-2 z-50 anim-in">
                <div className="px-3 py-2 text-sm font-medium truncate">{user.first_name || user.email}</div>
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => setMenu(false)} className="md:hidden flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm"><n.icon size={16} />{n.label}</Link>
                ))}
                <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm" onClick={() => { logout(); nav("/"); }} data-testid="logout-btn">
                  <LogOut size={16} /> Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="dk-container py-8" data-testid="app-main">
        <Outlet />
      </main>

      {locked && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(220,230,223,.75)", backdropFilter: "blur(14px)" }} data-testid="privacy-lock">
          <div className="dk-card p-8 max-w-sm text-center space-y-4">
            <span className="grid place-items-center w-12 h-12 rounded-full mx-auto" style={{ background: "var(--sage-tint)" }}><Lock style={{ color: "var(--sage)" }} /></span>
            <h3 className="font-serif text-[22px] font-semibold">Privacy lock</h3>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>We hid this screen after a quiet moment to keep your family's details private. Tap to continue.</p>
            <button className="btn btn-primary w-full" onClick={() => { setLocked(false); resetIdle(); }} data-testid="privacy-unlock-btn">Unlock</button>
          </div>
        </div>
      )}
    </div>
  );
}
